import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useTranslation } from '../hooks/useTranslation';
import {
  GlobeIcon,
  ChevronDownIcon,
  ShieldIcon,
  LightningIcon,
  SupportIcon,
  VerifiedIcon,
} from './common/Icons';
import XmarketLogo from './icons/XmarketLogo';
import '../styles/Footer.css';

const ASSURANCES = [
  { id: 'escrow', Icon: ShieldIcon, title: 'Escrow protection', text: 'Funds released only once you confirm delivery' },
  { id: 'delivery', Icon: LightningIcon, title: 'Instant delivery', text: 'Most accounts and top-ups arrive in minutes' },
  { id: 'sellers', Icon: VerifiedIcon, title: 'Verified sellers', text: 'Every seller passes ID checks before listing' },
  { id: 'support', Icon: SupportIcon, title: '24/7 human support', text: 'Real people on disputes, not a chatbot' },
];

const PAY_LOGOS = [
  { src: '/main/payments/visa.svg', alt: 'Visa' },
  { src: '/main/payments/mastercard.svg', alt: 'Mastercard' },
  { src: '/main/payments/amex.svg', alt: 'American Express' },
  { src: '/main/payments/paypal.svg', alt: 'PayPal' },
  { src: '/main/payments/btc.svg', alt: 'Bitcoin' },
];

const Footer = () => {
  const { currentLanguage, changeLanguage, currentLanguageData, supportedLanguages } = useLanguage();
  const { t } = useTranslation();
  const [showLanguageDropdown, setShowLanguageDropdown] = useState(false);
  const languageRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (languageRef.current && !languageRef.current.contains(event.target)) {
        setShowLanguageDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <footer className="footer">
      <div className="footer-inner">
        <ul className="footer-assurances">
          {ASSURANCES.map(({ id, Icon, title, text }) => (
            <li key={id} className="footer-assurance">
              <span className="footer-assurance-icon" aria-hidden>
                <Icon color="currentColor" size={16} />
              </span>
              <span className="footer-assurance-body">
                <span className="footer-assurance-title">{title}</span>
                <span className="footer-assurance-text">{text}</span>
              </span>
            </li>
          ))}
        </ul>

        <div className="footer-top">
          <div className="footer-brand">
            <Link to="/" className="footer-logo" aria-label="XMARKET">
              <XmarketLogo className="footer-logo-svg" />
            </Link>
            <p className="footer-brand-text">
              Escrow-protected trading for accounts, currency, items and boosting.
            </p>
            <Link to="/auth?signin=true" className="footer-text-link">
              Sign in
            </Link>
          </div>

          <nav className="footer-nav-cols" aria-label="Footer">
            <div className="footer-col">
              <h3 className="footer-col-title">Company</h3>
              <ul className="footer-col-links">
                <li><Link to="/">Home</Link></li>
                <li><Link to="/profile/become-seller">Become a seller</Link></li>
                <li><Link to="/help">Help center</Link></li>
              </ul>
            </div>
            <div className="footer-col">
              <h3 className="footer-col-title">Legal</h3>
              <ul className="footer-col-links">
                <li><a href="#terms">Terms of service</a></li>
                <li><a href="#privacy">Privacy policy</a></li>
                <li><a href="#cookies">Cookies policy</a></li>
                <li><a href="#abuse">Report abuse</a></li>
              </ul>
            </div>
            <div className="footer-col">
              <h3 className="footer-col-title">Support</h3>
              <ul className="footer-col-links">
                <li><Link to="/messages">Messages</Link></li>
                <li><Link to="/profile/orders/purchased">Orders</Link></li>
                <li><Link to="/help">Help center</Link></li>
              </ul>
            </div>
          </nav>
        </div>

        <div className="footer-bottom">
          <p className="footer-copy">
            © {new Date().getFullYear()} XMARKET. All rights reserved.
          </p>
          <div className="footer-pay" aria-label="Payment methods">
            {PAY_LOGOS.map((logo) => (
              <img key={logo.alt} src={logo.src} alt={logo.alt} />
            ))}
          </div>
          <div className="footer-bottom-links">
            <a href="#terms">{t('footer.termsOfService') || 'Terms of service'}</a>
            <a href="#privacy">{t('footer.privacyPolicy') || 'Privacy policy'}</a>
            <div className="footer-language" ref={languageRef}>
              <button
                type="button"
                className="footer-language-btn"
                onClick={() => setShowLanguageDropdown(!showLanguageDropdown)}
              >
                <GlobeIcon color="currentColor" size={13} />
                <span>{currentLanguageData.nativeName}</span>
                <ChevronDownIcon color="currentColor" size={10} />
              </button>
              {showLanguageDropdown && (
                <div className="footer-language-menu">
                  {supportedLanguages.map((lang) => (
                    <button
                      key={lang.code}
                      type="button"
                      className={`footer-language-item${currentLanguage === lang.code ? ' active' : ''}`}
                      onClick={() => {
                        changeLanguage(lang.code);
                        setShowLanguageDropdown(false);
                      }}
                    >
                      {lang.nativeName}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
